
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { PRODUCTS, BillingInterval, getActivePrice } from '../stripe-config';

interface PaymentData {
  interval: BillingInterval;
  email: string;
  tier?: string;
  priceId?: string; // override the default price (e.g. for the Collaborator plan)
}

export function usePayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const handlePayment = useCallback(async (data: PaymentData) => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
      if (!stripePublicKey) throw new Error('Stripe public key not found');

      const stripe = await loadStripe(stripePublicKey);
      if (!stripe) throw new Error('Failed to initialize Stripe');

      const billingInterval = data.interval === 'lifetime' ? 'monthly' : data.interval;
      const activePrice = getActivePrice(billingInterval as 'monthly' | 'annual');
      // Allow a caller-supplied priceId to override the default (e.g. Collaborator plan)
      const resolvedPriceId = data.priceId || activePrice.priceId;

      const { data: checkoutData, error: functionError } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          tier: data.tier || (data.interval === 'lifetime' ? 'lifetime' : 'monthly'),
          interval: data.interval,
          email: data.email,
          userId: user.id,
          priceId: resolvedPriceId
        }
      });

      if (functionError) {
        throw new Error(functionError.message || 'Failed to create checkout session');
      }

      if (!checkoutData?.url) {
        throw new Error('No checkout URL received');
      }

      const parsedUrl = new URL(checkoutData.url);
      if (!parsedUrl.hostname.endsWith('stripe.com')) {
        throw new Error('Invalid checkout URL');
      }

      localStorage.setItem('pendingUpgrade', JSON.stringify({
        interval: data.interval,
        timestamp: Date.now()
      }));

      return { url: checkoutData.url };

    } catch (err) {
      console.error('Payment error:', err);
      
      if (err instanceof Error && 
          (err.message.includes('Failed to fetch') || 
           err.message.includes('NetworkError'))) {
        
        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);
        
        if (newRetryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * newRetryCount));
          return handlePayment(data);
        }
      }
      
      setError(err instanceof Error ? err.message : 'Failed to process payment');
      setLoading(false);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [navigate, retryCount, maxRetries]);

  return {
    loading,
    error,
    retryCount,
    handlePayment
  };
}
